import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  ExternalLink, 
  Calendar, 
  User, 
  Tag, 
  Eye, 
  Lightbulb, 
  Loader2,
  AlertCircle 
} from 'lucide-react';
import TrustBadge from '@/components/common/TrustBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PublicSharePage = () => {
  const { token } = useParams();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSharedContent = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/share/${token}`);
        if (!res.ok) {
          try {
            const err = await res.json();
            throw new Error(err.detail || 'Share link not found');
          } catch {
            throw new Error('Share link not found or has been revoked');
          }
        }
        const data = await res.json();
        setContent(data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSharedContent();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A1024] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-red-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A1024] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Content Unavailable</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link 
            to="/" 
            className="text-red-400 hover:text-red-300 hover:underline font-medium"
          >
            Go to dotbox
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1024]">
      {/* Header */}
      <header className="bg-[#0F1730] border-b border-[#1F2A4D]">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-white tracking-tight" data-testid="public-share-logo">
            dotbox
          </Link>
          <div className="flex items-center gap-3 text-sm text-gray-400">
            <Eye className="h-4 w-4" />
            <span data-testid="public-share-views">{content.view_count} views</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <article className="bg-[#0F1730] rounded-lg border border-[#1F2A4D] shadow-sm overflow-hidden">
          {/* Title Section */}
          <div className="p-6 pb-4">
            <h1 
              className="text-2xl font-bold text-slate-100 leading-tight mb-3"
              data-testid="public-share-title"
            >
              {content.title}
            </h1>

            {/* Meta info */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              {content.source_name && (
                <span className="flex items-center gap-1.5">
                  <Tag className="h-3.5 w-3.5" />
                  {content.source_name}
                </span>
              )}
              {content.author && (
                <span className="flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" />
                  {content.author}
                </span>
              )}
              {content.published_at && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {new Date(content.published_at).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </span>
              )}
              {content.sentiment && (
                <Badge variant="outline" className="capitalize text-xs">
                  {content.sentiment}
                </Badge>
              )}
              {/* iter178 — Trust Badge: renders only when the publisher's
                  account is verified. Reader-visible signal that a real
                  human is behind the content. */}
              <TrustBadge trust={content.trust} />
            </div>
          </div>

          <Separator />

          {/* Summary / Content */}
          <div className="p-6 space-y-5">
            {content.ai_summary && (
              <div className="bg-blue-950/40 border border-blue-100 rounded-md p-4" data-testid="public-share-ai-summary">
                <p className="text-sm font-medium text-blue-200 mb-1">AI Summary</p>
                <p className="text-sm text-blue-300 leading-relaxed">{content.ai_summary}</p>
              </div>
            )}

            {content.summary && (
              <div data-testid="public-share-summary">
                <p className="text-slate-200 leading-relaxed">{content.summary}</p>
              </div>
            )}

            {content.content && content.content !== content.summary && (
              <div data-testid="public-share-content">
                <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{content.content}</p>
              </div>
            )}

            {/* Highlights */}
            {content.highlights && content.highlights.length > 0 && (
              <div className="bg-amber-50 border border-amber-100 rounded-md p-4" data-testid="public-share-highlights">
                <p className="text-sm font-medium text-amber-800 mb-3 flex items-center gap-1.5">
                  <Lightbulb className="h-4 w-4" />
                  Key Highlights
                </p>
                <ul className="space-y-2">
                  {content.highlights.map((h, i) => (
                    <li key={`hl-${i}-${(h || '').slice(0, 32)}`} className="text-sm text-amber-700 pl-4 border-l-2 border-amber-300">
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tags */}
            {content.tags && content.tags.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="public-share-tags">
                {content.tags.map((tag) => (
                  <Badge key={`tag-${tag}`} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Footer with source link */}
          {content.url && (
            <>
              <Separator />
              <div className="p-6">
                <a
                  href={content.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-red-400 hover:text-red-400/80 transition-colors"
                  data-testid="public-share-source-link"
                >
                  <ExternalLink className="h-4 w-4" />
                  View original source
                </a>
              </div>
            </>
          )}
        </article>

        {/* Shared via dotbox */}
        <div className="text-center mt-8 text-sm text-gray-400">
          Shared via{' '}
          <Link to="/" className="text-red-400 hover:underline font-medium">
            dotbox
          </Link>
        </div>
      </main>
    </div>
  );
};

export default PublicSharePage;
